import { apiFetch } from './api.js';
import { mountAssistant } from './assistant.js';
import {
  DB, GRADES, US_STATES, session, sessionMode, currentView, viewingAsAdmin,
  setSession, setCsrfToken, setSessionMode, setPrograms, setSchools, setStoreSales, setCoachingEvents, setCoachingMine,
  setCurrentParentId, setCurrentPerformerId, setCurrentHelpSlug, setViewingAsAdmin,
} from './state.js';
import { isAdmin, isParent, isValidEmail, hasAddress } from './domain-utils.js';
import { buildNav, route, refresh, VIEWS, VIEW_RESTORE } from './routing.js';
import { openModal, closeModal, toast, toastError } from './ui.js';

export function showAuth(which) {
  document.getElementById('authStage').style.display = '';
  ['login','reset','forcePass','signup','newPass'].forEach(k => document.getElementById(k + 'Card').style.display = (k === which ? '' : 'none'));
  const firstField = which === 'login' ? 'liEmail' : which === 'forcePass' ? 'fpNew'
    : which === 'signup' ? 'suFirst' : which === 'newPass' ? 'npNew' : 'rsEmail';
  const el = document.getElementById(firstField);
  if (el) el.focus();
}
// The emailed reset link lands on portal.html?reset=<token>. Redeeming it is
// the only thing that actually changes the password -- requesting the email
// leaves the old one working, so a reset someone else asked for is harmless.
let pendingResetToken = '';

export function showResetConfirm(token) {
  pendingResetToken = token;
  document.getElementById('authStage').style.display = '';
  showAuth('newPass');
}

export async function doResetConfirm() {
  const newPass = document.getElementById('npNew').value;
  const confirm = document.getElementById('npConfirm').value;
  const err = document.getElementById('npErr');
  if (newPass.length < 8) { err.textContent = 'Password must be at least 8 characters.'; err.classList.add('show'); return; }
  if (newPass !== confirm) { err.textContent = 'Passwords do not match.'; err.classList.add('show'); return; }
  err.classList.remove('show');
  try {
    const res = await fetch('/api/auth/reset-password/confirm', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: pendingResetToken, newPassword: newPass }),
    });
    const data = await res.json();
    if (!res.ok) { err.textContent = data.error || 'Could not reset your password.'; err.classList.add('show'); return; }
    pendingResetToken = '';
    showAuth('login');
    toast('Password reset — please sign in.');
  } catch (e) {
    err.textContent = 'Could not reach the server. Please try again.';
    err.classList.add('show');
  }
}

export function showForcePasswordChange() {
  document.getElementById('authStage').style.display = '';
  showAuth('forcePass');
}
export async function doForcePasswordChange() {
  const newPass = document.getElementById('fpNew').value;
  const confirm = document.getElementById('fpConfirm').value;
  const err = document.getElementById('fpErr');
  if (newPass.length < 8) { err.textContent = 'Password must be at least 8 characters.'; err.classList.add('show'); return; }
  if (newPass !== confirm) { err.textContent = 'Passwords do not match.'; err.classList.add('show'); return; }
  err.classList.remove('show');
  try {
    await apiFetch('/auth/change-password', { method: 'POST', body: JSON.stringify({ newPassword: newPass }) });
    session.mustChangePassword = false;
    try { await loadDataFromAPI(); } catch (e) { console.error('Data load failed:', e); }
    startSession(session);
    toast('Password set — welcome!');
  } catch (e) {
    err.textContent = e.message || 'Could not set password. Please try again.';
    err.classList.add('show');
  }
}
export async function doLogin() {
  const email = document.getElementById('liEmail').value.trim().toLowerCase();
  const password = document.getElementById('liPass').value;
  const err = document.getElementById('liErr');
  if (!email || !password) { err.classList.add('show'); return; }
  try {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) {
      err.textContent = data.error || 'Invalid email or password.';
      err.classList.add('show');
      return;
    }
    err.classList.remove('show');
    setCsrfToken(data.csrfToken);
    const user = {
      id: data.user.id, firstName: data.user.firstName, lastName: data.user.lastName,
      email: data.user.email, phone: data.user.phone || '',
      address: data.user.address || '', city: data.user.city || '',
      state: data.user.state || '', zip: data.user.zip || '',
      roles: data.user.role === 'both' ? ['parent','admin'] : [data.user.role],
      adminRole: data.user.adminRole, mustChangePassword: data.user.mustChangePassword,
      active: true, card: data.user.card || null, stripeId: data.user.stripeId || '',
      assistant: !!data.assistant
    };
    setSessionMode(isAdmin(user) ? 'admin' : 'parent');
    setSession(user);
    if (user.mustChangePassword) {
      showForcePasswordChange();
      return;
    }
    try {
      await loadDataFromAPI();
    } catch (loadErr) {
      console.error('Data load failed:', loadErr);
    }
    startSession(user);
  } catch (e) {
    err.textContent = e.message || 'Could not reach the server. Please try again.';
    err.classList.add('show');
  }
}
export async function doSelfRegister() {
  const firstName = document.getElementById('suFirst').value.trim();
  const lastName  = document.getElementById('suLast').value.trim();
  const email     = document.getElementById('suEmail').value.trim().toLowerCase();
  const phone     = document.getElementById('suPhone').value.trim();
  const password  = document.getElementById('suPass').value;
  const err = document.getElementById('suErr');
  if (!firstName || !lastName || !email || !password) {
    err.textContent = 'First name, last name, email, and password are required.';
    err.classList.add('show');
    return;
  }
  try {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName, lastName, email, phone, password })
    });
    const data = await res.json();
    if (!res.ok) {
      err.textContent = data.error || 'Could not create your account.';
      err.classList.add('show');
      return;
    }
    err.classList.remove('show');
    setCsrfToken(data.csrfToken);
    const user = {
      id: data.user.id, firstName: data.user.firstName, lastName: data.user.lastName,
      email: data.user.email, phone: data.user.phone || '',
      address: data.user.address || '', city: data.user.city || '',
      state: data.user.state || '', zip: data.user.zip || '',
      roles: ['parent'], adminRole: null, mustChangePassword: false,
      active: true, card: data.user.card || null, stripeId: data.user.stripeId || ''
    };
    setSessionMode('parent');
    setSession(user);
    try {
      await loadDataFromAPI();
    } catch (loadErr) {
      console.error('Data load failed:', loadErr);
    }
    startSession(user);
    toast('Account created — welcome!');
  } catch (e) {
    err.textContent = e.message || 'Could not reach the server. Please try again.';
    err.classList.add('show');
  }
}

export async function loadDataFromAPI() {
  const isAdminUser = isAdmin(session);
  const fetches = [
    apiFetch('/programs'),
    apiFetch('/schools'),
  ];
  if (isAdminUser) {
    fetches.push(
      apiFetch('/settings'),
      apiFetch('/accounts'),
      apiFetch('/performers'),
      apiFetch('/charges'),
      apiFetch('/enrollments'),
      apiFetch('/registrations'),
      apiFetch('/email-log'),
      apiFetch('/contracts'),
      apiFetch('/store/sales?includeArchived=1'),
      apiFetch('/coaching/events?includeArchived=1'),
    );
  } else {
    fetches.push(
      apiFetch('/settings/public'),
      apiFetch('/accounts/' + session.id),
      apiFetch('/accounts/' + session.id + '/performers'),
      apiFetch('/charges?accountId=' + session.id),
      apiFetch('/enrollments'),
      apiFetch('/store/sales'),
      apiFetch('/coaching/my'),
      // Appended last on purpose: the results[] indices below are positional,
      // so a new fetch has to go on the end or every assignment shifts.
      apiFetch('/settings/family'),
    );
  }
  const results = await Promise.all(fetches);

  const programs = results[0];
  DB.settings.programs = programs;
  setPrograms(programs);

  setSchools(results[1]);

  const mapEnrollment = se => ({
    id: se.id, performerId: se.performerId, programId: se.programId,
    seasonYear: se.seasonYear, status: se.status, billingMode: se.billingMode,
    rateOverride: se.rateOverride ?? null,
    registeredAt: se.createdAt, materialsFeePaid: false, contractSent: false, contractSigned: false
  });

  if (isAdminUser) {
    const settings = results[2];
    Object.assign(DB.settings, settings);
    DB.settings.programs = programs;
    DB.users = results[3];
    DB.children = results[4];
    DB.charges = results[5];
    DB.seasonEnrollments = results[6].map(mapEnrollment);
    DB.registrations = results[7];
    DB.emailLog = results[8];
    DB.contracts = results[9] || [];
    setStoreSales(results[10] || []);
    setCoachingEvents(results[11] || []);
  } else {
    const pubSettings = results[2];
    Object.assign(DB.settings, pubSettings);
    DB.settings.programs = programs;
    DB.users = [results[3]];
    DB.children = results[4];
    DB.charges = results[5];
    DB.seasonEnrollments = (results[6] || []).map(mapEnrollment);
    setStoreSales(results[7] || []);
    setCoachingMine(results[8] || { events: [], requests: [] });
    // Merged after pubSettings so it can only add keys /settings/public omits.
    Object.assign(DB.settings, results[9] || {});
  }
}

// PASSWORD POLICY (backend-enforced) — applies identically to parents and admins,
// at both initial password set and reset. The real "set new password" screen (reached
// from the reset link) POSTs to a server endpoint that MUST enforce:
//   1. Minimum 8 characters.
//   2. No forced complexity rules (do NOT require mixed upper/lower/number/symbol).
//   3. Reject common/breached passwords (e.g. check against the HaveIBeenPwned range API).
//   4. Allow long passphrases and password managers — never block paste, no max-length cap.
//   5. Hash with bcrypt before storing; fire the password_changed email on success.
// Enforcement must be server-side (client-side checks are bypassable and only advisory).
export async function doReset() {
  const email = document.getElementById('rsEmail').value.trim();
  if (!isValidEmail(email)) { toast('Please enter a valid email address'); return; }
  try {
    await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
  } catch (e) {}
  document.getElementById('rsNotice').classList.add('show');
}
export function switchMode(mode) {
  setSessionMode(mode);
  buildNav();
  route(mode === 'admin' ? 'a-home' : 'p-home');
}
export function startSession(user) {
  setSession(user);
  // Record last login in UTC for parent accounts
  if (isParent(user)) user.lastLogin = new Date().toISOString();
  document.getElementById('authStage').style.display = 'none';
  document.getElementById('shell').classList.add('show');
  document.getElementById('userName').textContent = user.firstName + ' ' + user.lastName;
  document.getElementById('userRole').textContent = isAdmin(user) && sessionMode === 'admin'
    ? (user.adminRole === 'view_only' ? 'View-Only Admin' : 'Administrator')
    : 'Parent / Guardian';
  document.getElementById('brandRole').textContent = sessionMode === 'admin' ? 'Admin Console' : 'Parent Account';
  mountAssistant(user.assistant);
  buildNav();
  const hashParts = location.hash.replace('#', '').split('/');
  const savedView = hashParts[0];
  if (hashParts[1]) {
    if (savedView === 'a-performer-detail') setCurrentPerformerId(hashParts[1]);
    else if (savedView === 'a-parent-detail') setCurrentParentId(hashParts[1]);
    else if (savedView === 'a-help-doc') setCurrentHelpSlug(hashParts[1]);
    else if (VIEW_RESTORE[savedView]) VIEW_RESTORE[savedView](hashParts.slice(1));
  }
  const defaultView = sessionMode === 'admin' ? 'a-home' : 'p-home';
  // Only restore saved view if it matches the current mode (admin pages for admins, parent pages for parents)
  const validView = savedView && VIEWS[savedView]
    && ((sessionMode === 'admin' && savedView.startsWith('a-')) || (sessionMode === 'parent' && savedView.startsWith('p-')));
  route(validView ? savedView : defaultView);
  showMissingInfoPrompt();
}
// Fires once per login for parents, covering everything the office would
// otherwise have to chase by email: grade/school per performer, plus the
// household mailing address (collected at registration but blank for every
// family that predates accounts.address, and for returning families who
// never filled out the public form at all). Always dismissible -- this is a
// nudge, not a gate.
export function showMissingInfoPrompt() {
  if (sessionMode === 'admin') return;
  // An admin viewing-as a family can't save (writes are blocked server-side
  // and client-side), so prompting them to fix that family's data is a dead
  // end -- and worse, it would put someone else's data in an editable-looking
  // form.
  if (viewingAsAdmin) return;
  const incomplete = DB.children.filter(k => !k.grade || !k.school);
  const needsAddress = !hasAddress(session);
  if (!incomplete.length && !needsAddress) return;
  const addressFields = !needsAddress ? '' : `
    <div style="border:1px solid var(--border-soft);border-radius:10px;padding:16px;margin-bottom:12px">
      <div style="font-size:14px;color:var(--cream);margin-bottom:12px">Mailing address</div>
      <div class="field"><label>Address</label><input type="text" id="mip_address" value="${esc(session.address || '')}" placeholder="123 Main St"></div>
      <div class="field-row">
        <div class="field"><label>City</label><input type="text" id="mip_city" value="${esc(session.city || '')}" placeholder="Orem"></div>
        <div class="field"><label>State</label>
          <select id="mip_state">
            <option value="">—</option>
            ${US_STATES.map(st => `<option value="${st}" ${(session.state || 'UT') === st ? 'selected' : ''}>${st}</option>`).join('')}
          </select>
        </div>
        <div class="field"><label>ZIP</label><input type="text" id="mip_zip" inputmode="numeric" value="${esc(session.zip || '')}" placeholder="84057"></div>
      </div>
    </div>`;
  const fields = addressFields + incomplete.map(k => `
    <div style="border:1px solid var(--border-soft);border-radius:10px;padding:16px;margin-bottom:12px">
      <div style="font-size:14px;color:var(--cream);margin-bottom:12px">${esc(k.firstName)} ${esc(k.lastName)}</div>
      <div class="field-row">
        <div class="field"><label>Grade (this fall)</label>
          <select id="mip_grade_${k.id}">
            <option value="">—</option>
            ${GRADES.map(g => `<option value="${esc(g)}" ${k.grade === g ? 'selected' : ''}>${esc(g)}</option>`).join('')}
          </select>
        </div>
        <div class="field"><label>School</label>
          <input type="text" id="mip_school_${k.id}" data-school-input autocomplete="off" value="${esc(k.school || '')}" placeholder="Start typing a school name">
        </div>
      </div>
    </div>`).join('');
  const intro = incomplete.length && needsAddress
    ? "We're missing a few details for your family. Mind filling them in?"
    : needsAddress
      ? "We don't have a mailing address on file for your family. Mind adding it?"
      : `We're missing some info for ${incomplete.length === 1 ? 'one of your performers' : 'some of your performers'}. Mind filling it in?`;
  openModal(`
    <h3>${needsAddress && !incomplete.length ? 'Help us keep your details current' : 'Help us get to know your performers'}</h3>
    <p class="card-intro">${intro}</p>
    ${fields}
    <div style="display:flex;gap:10px;margin-top:6px">
      <button class="btn" style="flex:1" onclick="saveMissingInfo()">Save</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">I'll do this later</button>
    </div>`, true);
}
export async function saveMissingInfo() {
  const incomplete = DB.children.filter(k => !k.grade || !k.school);
  const btn = document.querySelector('#modal .btn:not(.btn-ghost)');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    // Address first: if a performer PUT fails partway through, the family at
    // least doesn't lose the address they just typed.
    const addressEl = document.getElementById('mip_address');
    if (addressEl) {
      const v = id => (document.getElementById(id)?.value || '').trim();
      const address = addressEl.value.trim();
      if (address || v('mip_city') || v('mip_zip')) {
        const updated = await apiFetch('/accounts/' + session.id, {
          method: 'PUT',
          body: JSON.stringify({ address, city: v('mip_city'), state: v('mip_state'), zip: v('mip_zip') })
        });
        Object.assign(session, {
          address: updated.address || '', city: updated.city || '',
          state: updated.state || '', zip: updated.zip || ''
        });
      }
    }
    for (const k of incomplete) {
      const grade = document.getElementById('mip_grade_' + k.id)?.value || '';
      const school = document.getElementById('mip_school_' + k.id)?.value.trim() || '';
      if (!grade && !school) continue;
      const payload = { firstName: k.firstName, lastName: k.lastName, dob: k.dob, grade, school };
      const updated = await apiFetch('/performers/' + k.id, { method: 'PUT', body: JSON.stringify(payload) });
      Object.assign(k, updated, { name: updated.firstName + ' ' + updated.lastName });
    }
    closeModal();
    refresh();
    toast('Thanks for updating!');
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = 'Save'; }
    toastError(e.message);
  }
}
// ── View as parent (read-only admin impersonation) ────────────────────────
// Fully swaps `session` to a parent-shaped object (roles: ['parent']) and
// reuses the exact same loadDataFromAPI()/startSession() bootstrap a real
// parent login uses -- so what renders is provably the same parent SPA a
// real parent would see, not a bespoke admin-eyes copy. Because isAdmin(session)
// becomes false, buildNav() renders the real parent nav and every
// canEdit(session)-gated admin control disappears for free. Write endpoints
// are still blocked server-side (Middleware::enforceViewAsReadOnly) and
// client-side (api.js's apiFetch short-circuit) regardless of what renders.
export async function viewAsParent(accountId) {
  const adminUser = session;
  try {
    await apiFetch('/accounts/' + accountId + '/view-as', { method: 'POST' });
  } catch (e) { toastError(e.message); return; }
  let acct;
  try {
    acct = await apiFetch('/accounts/' + accountId);
  } catch (e) {
    toastError(e.message);
    // Roll back the server-side flag we just set — otherwise the server
    // thinks we're viewing-as (blocking writes) while the client never
    // learned that (no banner, no local viewingAsAdmin), a confusing desync.
    try { await apiFetch('/view-as/exit', { method: 'POST' }); } catch (_) {}
    return;
  }
  const parentUser = {
    id: acct.id, firstName: acct.firstName, lastName: acct.lastName,
    email: acct.email, phone: acct.phone || '',
    address: acct.address || '', city: acct.city || '', state: acct.state || '', zip: acct.zip || '',
    roles: ['parent'], adminRole: null, mustChangePassword: false,
    active: acct.active, card: acct.card || null, stripeId: acct.stripeId || ''
  };
  setViewingAsAdmin(adminUser);
  setSessionMode('parent');
  setSession(parentUser);
  try { await loadDataFromAPI(); } catch (e) { console.error('Data load failed:', e); }
  startSession(parentUser);
  showViewAsBanner(parentUser.firstName + ' ' + parentUser.lastName);
}
export async function exitViewAs() {
  const adminUser = viewingAsAdmin;
  if (!adminUser) return;
  try { await apiFetch('/view-as/exit', { method: 'POST' }); } catch (e) {}
  setViewingAsAdmin(null);
  setSessionMode('admin');
  setSession(adminUser);
  try { await loadDataFromAPI(); } catch (e) { console.error('Data load failed:', e); }
  startSession(adminUser);
  hideViewAsBanner();
  route('a-parent-detail');
}
function showViewAsBanner(name) {
  const el = document.getElementById('viewAsBanner');
  if (!el) return;
  el.innerHTML = `<span>Viewing as <b>${esc(name)}</b> — read only</span><button id="viewAsExitBtn" style="background:#0e0b16;color:var(--gold-light);border:none;border-radius:7px;padding:6px 14px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit">Exit View As</button>`;
  el.classList.add('show');
  document.getElementById('viewAsExitBtn').addEventListener('click', exitViewAs);
}
function hideViewAsBanner() {
  const el = document.getElementById('viewAsBanner');
  if (el) { el.classList.remove('show'); el.innerHTML = ''; }
}

// message: why they were signed out (a deactivated account), shown on the
// sign-in screen.
export function resetToLogin(message) {
  setSession(null);
  setCsrfToken('');
  setSessionMode('admin');
  setViewingAsAdmin(null);
  hideViewAsBanner();
  DB.users = [];
  DB.children = [];
  DB.charges = [];
  DB.seasonEnrollments = [];
  DB.registrations = [];
  DB.emailLog = [];
  DB.contracts = [];
  location.hash = '';
  document.getElementById('shell').classList.remove('show');
  document.getElementById('authStage').style.display = '';
  showAuth('login');
  // Never cleared here: the 401s from requests already in flight would wipe
  // the message. A successful sign-in hides it (doLogin).
  if (typeof message === 'string' && message) {
    const err = document.getElementById('liErr');
    err.textContent = message;
    err.classList.add('show');
  }
}
export async function logout() {
  try { await apiFetch('/auth/logout', { method: 'POST' }); } catch (e) {}
  resetToLogin();
}

Object.assign(window, {
  showAuth, showForcePasswordChange, doForcePasswordChange, doLogin, doSelfRegister,
  loadDataFromAPI, doReset, doResetConfirm, showResetConfirm, switchMode, startSession, showMissingInfoPrompt,
  saveMissingInfo, resetToLogin, logout,
});
